import React, { useState, useRef, useEffect, useCallback } from 'react';
import CardVideoPlayer from './CardVideoPlayer';
import { playableUrl } from '../utils/playableUrl';
import { reloadIfLinksExpired } from '../utils/expiredLinks';
import { MdOutlineZoomOutMap, MdOutlinePlayCircle, MdTune, MdKeyboardArrowRight, MdKeyboardArrowLeft } from "react-icons/md";
import { IoSettingsSharp, IoLocationOutline } from "react-icons/io5";
import { MdOutlinePermMedia, MdPictureInPicture, MdCloseFullscreen } from "react-icons/md";
import { GrMultimedia } from "react-icons/gr";
import { CiCalendarDate } from "react-icons/ci";
import { IoIosResize } from "react-icons/io";
import { RiDeleteBinLine } from "react-icons/ri";
import { IoShareSocialOutline } from "react-icons/io5";
import { FiEdit2 } from "react-icons/fi";

/**
 * Single card in the View All grid/list.
 * Isolated per-card state (hover, settings, quality/speed menus) prevents
 * the full list from re-rendering on every mouse movement or popup open.
 *
 * All parent-level callbacks must be stable (useCallback) so React.memo can
 * skip re-renders when only unrelated parent state changes.
 */
const VideoCard = React.memo(function VideoCard({
  data,
  index,
  videoRefsMap,
  viewType,
  selectedQuality,
  currentPlaybackRate,
  volume,
  isMuted,
  searchType,
  isAuthenticated,
  userData,
  isPiP,
  isChecked,
  speeds,
  onPopupTagShow,
  onTogglePiP,
  onCheckboxChange,
  onListPopup,
  onDelete,
  onShare,
  showShare = true,
  onVolumeChange,
  onQualityChange,
  onPlaybackRateChange,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [activeSettings, setActiveSettings] = useState(false);
  // true = panel hidden (default), false = panel visible — same semantics as original parent state
  const [quality, setQuality] = useState(true);
  const [speedFlag, setSpeedFlag] = useState(true);
  const popupRef = useRef(null);
  const dropdownRef = useRef(null);

  // Only register the document listener while this card's settings panel is open.
  // Guarding on activeSettings means 0 listeners when no settings are open across
  // the entire list — important when infinite scroll accumulates 100+ cards.
  useEffect(() => {
    if (!activeSettings) return;
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setActiveSettings(false);
        setSpeedFlag(true);
        setQuality(true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeSettings]);

  const toggleSettings = useCallback(() => {
    setActiveSettings(prev => !prev);
    setIsHovered(false);
    setSpeedFlag(true);
    setQuality(true);
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setActiveSettings(false);
    setSpeedFlag(true);
    setQuality(true);
  }, []);

  const handleShowQuality = useCallback(() => setQuality(prev => !prev), []);
  const handleBackToQuality = useCallback(() => setQuality(true), []);
  const handleShowSpeed = useCallback(() => setSpeedFlag(prev => !prev), []);
  const handleBackToList = useCallback(() => setSpeedFlag(true), []);

  // Stable callbacks for CardVideoPlayer so its React.memo can skip re-renders
  // when only VideoCard's own state changes (settings toggle, quality/speed menus).
  const handlePlay = useCallback(() => {
    if (!isAuthenticated) { window.location.href = "/login"; }
  }, [isAuthenticated]);

  const handleContextMenu = useCallback((event) => event.preventDefault(), []);

  // Segment clamp ONLY in "Search by segment" mode: searchType === 1 is the one
  // value for which useSearch requests the per-segment branch. The default is
  // null (checkbox never touched) and unticking gives 0 — both are whole-video
  // rows whose start/end are MAX() over segments, so seeking to them started a
  // 96 s video at 80 s and stopped it at 90 s (`!= 0` treated null as segment mode).
  const inSegmentMode = searchType === 1;
  const handleTimeUpdate = useCallback((e) => {
    const video = e.target;
    if (inSegmentMode && data.end_time_totalseconds && video.currentTime >= data.end_time_totalseconds) {
      video.pause();
      video.currentTime = data.start_time_totalseconds || 0;
    }
  }, [inSegmentMode, data.end_time_totalseconds, data.start_time_totalseconds]);

  const handleLoadedMetadata = useCallback((e) => {
    if (inSegmentMode) {
      const video = e.target;
      video.currentTime = data.start_time_totalseconds || 0;
    }
  }, [inSegmentMode, data.start_time_totalseconds]);

  return (
    <>
      <div className='position-relative'>
        <div className={`card ${viewType === 'grid' ? '' : 'list-view'}`}>
          <div className='position-relative loader-image-div'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {data.file_type_id === 1 ? (
              <div className="position-relative image-container">
                <img className="card-img-top media-no-save" draggable={false} alt="..."
                  src={(data?.thumbnail_image) ? data.thumbnail_image : data.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onClick={() => onPopupTagShow(data)}
                  onError={(e) => { if (reloadIfLinksExpired(e.target.src)) return; e.target.onerror = null; if (data.url) e.target.src = data.url; }} />
                <MdOutlineZoomOutMap className='img-zoom' onClick={() => onPopupTagShow(data)} />
              </div>
            ) : (
              <>
                <CardVideoPlayer
                  ref={(el) => { videoRefsMap.current[data.id] = el; }}
                  url={playableUrl(data, selectedQuality)}
                  poster={data?.thumbnail_image}
                  playing={isAuthenticated && isHovered}
                  controls={isHovered}
                  playbackRate={currentPlaybackRate}
                  volume={volume}
                  muted={isMuted}
                  className="w-100"
                  onVolumeChange={onVolumeChange}
                  onPlay={handlePlay}
                  onContextMenu={handleContextMenu}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                />

                {(isHovered || activeSettings) && (
                  <>
                    <div className='position-relative' ref={popupRef}>
                      <button
                        onClick={toggleSettings}
                        className="settings-btn-qta"
                      >
                        <IoSettingsSharp className={`active-settings-btn ${activeSettings ? "active" : ''}`} />
                      </button>
                      {activeSettings && (
                        <div className={`video-control-popup ${speedFlag && quality ? '' : 'active'}`}>
                          <div className='show-playback-list'>
                            <div className="playback-btn" onClick={handleShowQuality}>
                              <MdTune />
                              Quality
                              <div className='popup-list-items'>
                                <span className='popup-list-quality'>({selectedQuality}p)</span>
                                <MdKeyboardArrowRight />
                              </div>
                            </div>
                            <div className="playback-btn" onClick={handleShowSpeed}>
                              <MdOutlinePlayCircle />
                              Playback speed
                              <div className='popup-list-items'>
                                {currentPlaybackRate}
                                <MdKeyboardArrowRight />
                              </div>
                            </div>
                            <div className="playback-btn" onClick={() => onTogglePiP(data.id)}>
                              {isPiP
                                ? <>
                                  <MdCloseFullscreen />
                                  Exit PiP
                                </>
                                : <>
                                  <MdPictureInPicture />
                                  Picture-in-Picture
                                </>
                              }
                            </div>
                          </div>
                          <div ref={dropdownRef} className={`quality-dropdown-qta ${quality ? '' : 'active'}`}>
                            <div className='playback-btn palback-back-div' onClick={handleBackToQuality}>
                              <MdKeyboardArrowLeft />
                              Quality
                            </div>
                            {data?.qualityTypeArray?.map((q) => (
                              <>
                                <p key={q.qualitytype} className="quality-dropdown-p"
                                  onClick={() => onQualityChange(q.qualitytype)}>
                                  <input
                                    type="radio"
                                    id={`${data.id}-${q.qualitytype}`}
                                    className="quality-radio-input"
                                    name="global-quality"
                                    value={q.qualitytype}
                                    checked={selectedQuality === q.qualitytype}
                                    defaultChecked={selectedQuality}
                                    onChange={() => onQualityChange(q.qualitytype)}
                                  />
                                  <label htmlFor={`${data.id}-${q.qualitytype}`}>{q.qualitytype}</label>
                                </p>
                              </>
                            ))}
                          </div>
                          <div className={`show-playback-items ${speedFlag ? '' : 'active'}`}>
                            <div className='playback-btn palback-back-div' onClick={handleBackToList}>
                              <MdKeyboardArrowLeft />
                              Playback speed
                            </div>
                            {speeds.map((s) => (
                              <div
                                key={s.value}
                                className={`playback-btn ${currentPlaybackRate === s.value ? 'selected' : ''}`}
                                onClick={() => onPlaybackRateChange(s.value)}
                              >
                                {s.label}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {isAuthenticated &&
              <div className="adminIconMain">
                <div className='adminIconMainLt'>
                  {(userData?.roleDisplayName === "Admin" || userData?.roleDisplayName === "Super_admin" || userData?.roleDisplayName === "User" || userData?.roleDisplayName === "APPROVER") &&
                    <label className="custom-checkbox">
                      <input type="checkbox"
                        name={data.fileName}
                        checked={isChecked}
                        onChange={(e) => onCheckboxChange(e, data.video_id, data.file_type_id, data.id, data)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  }
                </div>
                <div className="adminIconMainRt position-relative">
                  {(userData?.roleDisplayName === "Admin" || userData?.roleDisplayName === "Super_admin" || userData?.roleDisplayName === "APPROVER") &&
                    <>
                      {/* While anything is ticked, the selection bar shares instead. */}
                      {showShare && (
                        <button className="editIcon" aria-label="Share" onClick={() => onShare(data)}>
                          <IoShareSocialOutline size={17} />
                        </button>
                      )}
                      <button className="editIcon" onClick={() => onListPopup(data, "Edit", index)}>
                        <FiEdit2 size={17} />
                      </button>
                      <button className="editIcon" onClick={() => onDelete(data)}>
                        <RiDeleteBinLine size={17} />
                      </button>
                    </>
                  }
                </div>
              </div>
            }
          </div>
          <div className="card-body" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            {((viewType === "list") && ((!isAuthenticated) || (isAuthenticated && userData?.roleDisplayName === "User"))) &&
              <div className='col-md-12 listBtnDiv'>
                {data.file_type_id && <span className="listBtnView">
                  {data.file_type_id === 2 ? (
                    <>
                      <GrMultimedia /> Video
                    </>
                  ) : (
                    <>
                      <MdOutlinePermMedia /> Image
                    </>
                  )}
                </span>}
                {data.video_date && <span className="listBtnView"><CiCalendarDate />{data.video_date}</span>}
                {data.video_location && <span className="listBtnView"><IoLocationOutline />{data.video_location}</span>}
                {data.size && <span className="listBtnView"><IoIosResize />{data.size}</span>}
              </div>
            }
            <h5 className="card-title">{data.video_heading}</h5>
            <p className="card-text">{data.description}</p>
          </div>
        </div>
      </div>
    </>
  );
});

export default VideoCard;
